<template>
  <div id="credit-card-success-page">
    <YboxIframe :amount=device.payment.amount :uuid=device.payment.planUuid />
    <div class="success-page__header">
      <img :src="iconSuccessURL" alt="Sucesso!">
      <h2>Parabéns! Sua {{ device.payment.isRenovation ? 'renovação' : 'compra' }} foi realizada!</h2>

      <p>
        Você receberá no e-mail <strong>{{ emailString }}</strong> a
        confirmação da sua {{ device.payment.isRenovation ? 'renovação' : 'compra' }} e o link de acesso para começar a
        utilizar o seu seguro.
      </p>

      <p>
        <strong>Atenção:</strong> sempre verifique a sua caixa de spam,
        pois o e-mail pode ser direcionado para lá.
      </p>
    </div>
    <div class="success-page__content">
      <div class="success-page__block success-page__block--warning">
        <h3>Informação importante</h3>
        <p>
          Para a validação final de seu seguro, precisamos que nos envie o
          {{ lblIdentification }}* do seu aparelho. Aproveite para fazer
          isso agora mesmo! Siga as instruções clicando no botão abaixo.
        </p>
        <div class="success-page__device-identification">
          <Button
            v-if="loadingDeviceIdentification"
            label="Cadastrar documento"
            icon="plus-purple"
            theme="purple"
            disabled
          />
          <Button
            v-else-if="confirmDeviceIdentification"
            label="Documento cadastrado"
            icon="success"
            theme="purple"
            disabled
          />
          <Button
            v-else
            @click="tracking"
            v-b-modal.modalDeviceIdentification
            label="Cadastrar documento"
            icon="plus-purple"
            theme="purple"
          />
          <span v-if="showDeviceIdentificationSuccessMessage" class="success-page__success-message">{{ lblIdentification }} cadastrado com sucesso!</span>
        </div>
      </div>

      <NPS />

      <div class="success-page__block">
        <h3>Resumo da {{ device.payment.isRenovation ? 'renovação' : 'compra' }}</h3>

        <div class="success-page__block__row" v-if="device.payment.typePayment !== 'VOUCHER'">
          <p>
            <span>Valor mensal:</span>
            <strong>{{ installmentPrice | currencyBRL }}</strong>
          </p>
        </div>

        <div
          class="success-page__block__row row-footer"
          :class="{'row-footer--no-margin': device.payment.typePayment === 'VOUCHER'}"
        >
          <p>
            <span>Valor total:</span>
            <strong>{{ device.payment.amount | currencyBRL }}</strong>
          </p>
        </div>

        <div class="success-page__block__row" v-if="device.scoringProgram">
          <div v-if="device.scoringProgram.pointsLivelo" class="selo-livelo">
            <img src="https://ciclic.imgix.net/logo/livelo.webp?auto=format,compress&w=48&h=48" alt="Logo livelo" />
            <span>Com este combo você acumula {{ device.scoringProgram.pointsLivelo }} pontos Livelo*</span>
          </div>
          <div v-if="device.scoringProgram.pointsDotz" class="selo-livelo">
            <img src="https://ciclic.imgix.net/logos/logo_dotz.png?auto=format,compress" alt="Logo livelo" />
            <span>Com este combo você acumula {{ device.scoringProgram.pointsDotz }} pontos Dotz*</span>
          </div>
        </div>

      </div>

      <div class="success-page__block">
        <span class="icon icon__card" v-if="device.payment.typePayment === 'VOUCHER'">Voucher</span>
        <span class="icon icon__card" v-else-if="device.payment.typePayment === 'PAYPAL'">PayPal</span>
        <span class="icon icon__card" v-else-if="device.payment.typePayment === 'GOOGLE_PAY'">Google Pay</span>
        <span class="icon icon__card" v-else>Cartão de Crédito</span>
        <div class="card-details" v-if="showCardBrand">
          <div class="card-details__icon">
            <img :src="iconCreditCard" alt="Ícone cartão de crédito">
          </div>
          <div class="card-details__title">
            <span>{{ device.payment.cardBrand }} ****{{ device.payment.lastcardNumbers }}</span>
          </div>
        </div>
      </div>

      <div class="success-page__block">
        <div class="success-page__accordion">
          <div class="success-page__accordion-head">
            <div class="success-page__accordion-head-title">
              <img :src="iconDeviceURL" alt="Aparelho">
              <span>{{ device.nome | capitalize }}</span>
            </div>

            <a class="terms" :href="termsLink" target="_blank">
              <img class="terms__icon" :src="iconDocument" alt="Ícone documento">
              <span class="terms__title">Condições gerais</span>
            </a>

            <button v-if="isDesktop" @click.prevent.stop="toggleDetails" :class="{'opened': showDetails}">
              <span>Ver detalhes</span>
            </button>
          </div>

          <div v-if="showDetails" class="success-page__accordion-content">
            <div class="success-page__accordion-content-row">
              <img :src="iconCoverageURL" alt="Cobertura">
              <p>
                <strong>Cobertura: </strong>
                <span>Roubo</span>
                <span v-if="somenteRoubo"> e Furto</span>
                <span v-else>, Furto, Quebra, Líquido</span>
              </p>
            </div>

            <div class="success-page__accordion-content-row">
              <img :src="iconValidityURL" alt="Vigência">
              <p>
                <strong>Vigência do seguro: </strong>
                <span>12 meses (Franquia de 25% do valor do aparelho parcelada em até 4x)</span>
              </p>
            </div>

            <div v-if="device.payment.policyNumber" class="success-page__accordion-content-row">
              <img :src="iconDocument" alt="Ícone documento">
              <p>
                <strong>Número da apólice de seguro: </strong>
                <span>{{ device.payment.policyNumber }}</span>
              </p>
            </div>

            <div v-if="device.payment.isRenovation" class="success-page__accordion-content-row">
              <img :src="iconCoverageURL" alt="Ícone documento">
              <p>
                <strong>{{ device.payment.isRenovation ? 'Seguro sem carência: ': 'Carência de 30 dias: '}}</strong>
                <span>Vigência iniciada em {{ device.renewalStartDate }}</span>
              </p>
            </div>

            <div v-if="hasProtectedBag" class="success-page__accordion-content-row">
              <img :src="iconCoverageURL" alt="Cobertura">
              <p>
                <strong>{{ device.protectedBag.bagName }} </strong>
                <span>(Transações digitais, pix e itens da bolsa)</span>
              </p>
              <a class="terms" :href="device.protectedBag.termsLink" target="_blank">
                <img class="terms__icon" :src="iconDocument" alt="Ícone documento">
                <span class="terms__title">Condições gerais</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="success-page__footer">
        <Button
          label="Continuar navegando"
          icon="chevron-left"
          theme="link"
          @click="$router.push('/')"
        />
      </div>
    </div>

    <b-modal
      id="modalDeviceIdentification" size="lg"
      centered
      hide-footer
      body-bg-variant="white">
      <ImeiCadastro
        v-if="device.deviceTypeName === 'celular'"
        :onSuccess="deviceIdentificationSuccess"
        @confirmDeviceIdentification="handleIMEI"
      />
      <SerialNumberRegistration
        v-else
        :onSuccess="deviceIdentificationSuccess"
        @confirmDeviceIdentification="handleIMEI"
      />
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import axios from 'axios'

import YboxIframe from '../YboxIframe/index.vue'
import ImeiCadastro from '@/components/ImeiCadastro/ImeiCadastro'
import SerialNumberRegistration from '@/components/SerialNumberRegistration'

import NPS from '@/components/NPS/index.vue'
import Button from '@/components/ui/Button'

export default {
  name: 'CreditCard',
  components: {
    YboxIframe,
    ImeiCadastro,
    SerialNumberRegistration,
    NPS,
    Button
  },
  data () {
    return {
      showDetails: false,
      isDesktop: true,
      confirmDeviceIdentification: false,
      loadingDeviceIdentification: true,
      showDeviceIdentificationSuccessMessage: false,
      serialNumber: null,
      invoiceFileName: null,
      termsLink: `${process.env.VUE_APP_CHICO_XAVIER}/Ciclic_SeguroCelular_Condicoes.pdf`
    }
  },
  computed: {
    ...mapState(['device']),
    somenteRoubo () {
      return this.device.somenteRoubo
    },
    iconCreditCard () {
      const cardBrand = this.device.payment.cardBrand || 'placeholder'
      return require(`@/assets/flags/${cardBrand.toLowerCase()}.png`)
    },
    iconSuccessURL () {
      return require(`@/assets/svg/icon-success-purple.svg`)
    },
    iconDeviceURL () {
      const name = this.device.deviceTypeName === 'celular' ? 'cellphone' : this.device.deviceTypeName
      return require(`@/assets/svg/icon-device-${name}.svg`)
    },
    iconCoverageURL () {
      return require(`@/assets/svg/icon-coverage-purple.svg`)
    },
    iconValidityURL () {
      return require(`@/assets/svg/icon-validity-purple.svg`)
    },
    iconDocument () {
      return require(`@/assets/svg/icon-document-purple.svg`)
    },
    emailString () {
      return this.device.personalData.email
        ? this.device.personalData.email
        : 'de cadastro '
    },
    lblIdentification () {
      return this.device.deviceTypeName === 'celular' ? 'IMEI' : 'número de série'
    },
    hasProtectedBag () {
      return !!this.device.protectedBag
    },
    installmentPrice () {
      return this.device.parcelasPrecos[this.device.payment.installment - 1]
    },
    showCardBrand () {
      return !!this.device.payment.cardBrand
    }
  },
  mounted () {
    const windowSize = window.innerWidth

    if (windowSize <= 768) {
      this.isDesktop = false
      this.showDetails = true
    }

    this.getDeviceIdentification()
  },
  methods: {
    toggleDetails () {
      this.showDetails = !this.showDetails
    },
    tracking () {
      window.dataLayer.push({
        event: 'dispositivos-ciclic',
        categoria: 'cadastro-dispositivo',
        acao: this.device.deviceTypeName === 'celular' ? 'IMEI' : 'numero-serie',
        rotulo: '1-continuar'
      })
    },
    handleIMEI (event) {
      this.confirmDeviceIdentification = event
      this.disparaDataLayer()
    },
    disparaDataLayer () {
      if (this.confirmDeviceIdentification && this.confirmaNF) {
        window.dataLayer.push({
          event: 'afiliattionTag',
          level: 'checkoutPage'
        })
      }
    },
    getDeviceIdentification () {
      axios
        .get(`${process.env.VUE_APP_API_URL}/internal-cell-phones/v1/policy/${this.device.payment.planUuid}`, JSON.parse(sessionStorage.config))
        .then((response) => {
          this.serialNumber = response.data.serialNumber
          this.invoiceFileName = response.data.invoiceFileName

          if (!this.serialNumber) {
            this.confirmDeviceIdentification = false
          } else {
            this.confirmDeviceIdentification = true
          }

          this.loadingDeviceIdentification = false
        })
        .catch(error => {
          this.$sentry.logWithContext({
            ctx: '[step/confirmation]', error
          })
        })
    },
    deviceIdentificationSuccess () {
      this.showDeviceIdentificationSuccessMessage = true

      setTimeout(() => {
        this.showDeviceIdentificationSuccessMessage = false
      }, 5000)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../SuccessPage.scss';

#credit-card-success-page {
  align-items: center;
  display: flex;
  flex-direction: column;

  .success-page__header {
    max-width: 752px;
    text-align: center;
    padding-top:32px;
    width: 100%;

    img {
      margin-bottom: 12px;
    }
  }

  .success-page__device-identification {
    display: grid;
    grid-auto-flow: column;
    grid-gap: 20px;
    justify-content: start;
    align-items: center;

    @media (max-width: 767px) {
      grid-auto-flow: row;
      grid-gap: 10px;
    }
  }

  @keyframes fade {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .success-page__success-message {
    color: #00d2be;
    font-weight: bold;
    pointer-events: none;
    animation: fade 0.5s ease-in-out;
  }

  .success-page__error-message {
    color: #ff3b30;
    font-weight: bold;
    pointer-events: none;
    animation: fade 0.5s ease-in-out;
  }

  .success-page__content {
    max-width: 944px;
    width: 100%;

    .success-page__block {
      align-items: center;
      flex-shrink: 0;
      flex-wrap: wrap;
      justify-content: flex-end;

      &--warning {
        display: block;
        background-color: transparent;
      }

      span {
        margin-right: auto;
      }

      &__row {
        flex-shrink: 0;
        flex-wrap: wrap;

        p {
          display: flex;
          margin: 0;
          width: 100%;

          + p {
            margin-top: 12px;
          }

          span {
            color: #444444;
            font-family: Rubik, sans-serif;
            font-style: normal;
            font-weight: 400;
            font-size: 16px;
            line-height: 19px;
          }

          strong {
            color: #444444;
            font-family: Rubik, sans-serif;
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 19px;
            margin-left: auto;
            text-align: right;
          }
        }
      }

      .success-page__accordion {
        width: 100%;

        &-head {
          align-items: center;
          display: flex;
          justify-content: space-between;
          margin-top: 10px;

          .terms {
            @media (min-width: 768px) {
              margin-right: auto;
            }
          }

          &-title {
            align-items: center;
            display: flex;
            justify-content: flex-start;

            img {
              margin-right: 16px;
              width: 32px;
            }

            span {
              color: #444444;
              font-family: Oscine, sans-serif;
              font-style: normal;
              font-weight: bold;
              font-size: 18px;
              line-height: 23px;
            }
          }

          button {
            &.opened {
              span {
                &::before {
                  transform: rotate(0);
                  transition: all ease 0.2s;
                }
              }
            }

            &:focus {
              outline:0;
            }

            span {
              display: inline-block;
              padding-right: 20px;
              position: relative;

              &::before {
                background-image: url(~@/assets/svg/chevron-up.svg);
                content: '';
                height: 7px;
                position: absolute;
                right: 0;
                top: calc(50% - 3.5px);
                transform: rotate(180deg);
                transition: all ease 0.2s;
                width: 14px;
              }
            }
          }
        }

        &-content {
          margin-top: 22px;

          &-row {
            align-items: center;
            border-top: solid 1px #BDC2C630;
            display: flex;
            padding: 20px 0;
            justify-content: flex-start;
            text-align: left;

            &:last-child {
              padding-bottom: 0;
            }

            > img {
              margin-right: 16px;
              width: 32px;
            }

            p {
              margin: 0;

              span {
                color: #444444;
                font-family: Oscine, sans-serif;
                font-style: normal;
                font-weight: 400;
                font-size: 16px;
                line-height: 21px;
              }

              strong {
                color: #444444;
                font-family: Oscine, sans-serif;
                font-style: normal;
                font-weight: 700;
                font-size: 16px;
                line-height: 21px;
              }
            }
          }
        }
      }
    }
  }

  .success-page__footer {
    align-items: center;
    display: flex;
    justify-content: center;
    padding: 30px 0;
  }

  .card-details {
    display: flex;
    align-items: center;

    @media (max-width: 767px) {
      width: 100%;
      margin-top: 16px;
    }

    &__icon {
      max-width: 34px;
      margin-right: 16px;

      img {
        width: 100%;
      }
    }

    &__title {
      line-height: 26px;
      text-transform: capitalize;
      font-family: 'Rubik', sans-serif;
      font-weight: 500;
      color: #595959;
    }
  }

  .terms {
    margin-left: auto;
    display: flex;
    align-items: center;
    font-weight: bold;
    color: #3F4956;

    @media (max-width: 959px) {
      font-size: 11px;
      flex-direction: column;
      text-align: center;
    }

    @media (max-width: 767px) {
      max-width: 67px;
    }

    &__icon {
      max-width: 20px;
      margin-right: 7px;

      @media (max-width: 959px) {
        margin-right: 0;
        margin-bottom: 3px;
      }
    }

    &__title {
      line-height: 1.1;
    }
  }

  .selo-livelo {
    margin-top: 24px;
    font-size: 14px;
    width: 100%;
    max-width: 300px;
    display: flex;
    align-items: center;
  }

  .selo-livelo img {
    margin-right: 16px;
    width: 48px;
  }
}
</style>
